import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GalleryLightbox from '../components/GalleryLightbox';
import { IconPin, IconCalendar } from '../components/Icons';
import useScrollReveal from '../hooks/useScrollReveal';
import './Gallery.css';

// slide animation variants
const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.98
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1
  },
  exit: (direction) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.98
  })
};

// Individual post card with its own carousel state
function PostCard({ post, i, onLightbox, getCategoryLabel }) {
  const [[page, direction], setPage] = useState([0, 0]);
  const images = Array.isArray(post.images) ? post.images : (post.src ? [post.src] : []);
  const activeIdx = page;

  const goTo = useCallback((newIdx) => {
    const clamped = Math.max(0, Math.min(images.length - 1, newIdx));
    const dir = clamped > activeIdx ? 1 : -1;
    setPage([clamped, dir]);
  }, [images.length, activeIdx]);

  return (
    <article id={`gallery-post-${post.id}`} className={`post-card reveal reveal-up delay-${(i % 3) + 1}`}>
      <div className="post-card__header">
        <span className="badge badge-navy">{getCategoryLabel(post.category)}</span>
        <div className="post-card__meta">
          {post.location && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconPin size={14} /> {post.location}</span>}
          {post.date     && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconCalendar size={14} /> {post.date}</span>}
        </div>
      </div>

      {images.length > 0 && (
        <div className="post-card__carousel-wrap">
          <div className="post-card__carousel">
            <AnimatePresence initial={false} custom={direction} mode="popLayout">
              <motion.div
                key={activeIdx}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 260, damping: 26 },
                  opacity: { duration: 0.2 }
                }}
                className="post-card__img-container"
              >
                <div className="post-card__img-wrapper">
                  <img
                    src={images[activeIdx]}
                    alt={`${post.caption || 'Gallery Image'} - ${activeIdx + 1}`}
                    className="post-card__img"
                    loading="lazy"
                    onClick={() => {
                      const postImagesArray = images.map(imgSrc => ({
                        src: imgSrc, caption: post.caption, category: post.category, location: post.location, date: post.date, tags: post.tags
                      }));
                      onLightbox({ images: postImagesArray, index: activeIdx });
                    }}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {images.length > 1 && (
            <>
              <button className="post-card__nav post-card__nav--prev" onClick={() => goTo(activeIdx - 1)} aria-label="Previous photo" disabled={activeIdx === 0}>
                &#8249;
              </button>
              <button className="post-card__nav post-card__nav--next" onClick={() => goTo(activeIdx + 1)} aria-label="Next photo" disabled={activeIdx === images.length - 1}>
                &#8250;
              </button>
              <div className="post-card__dots">
                {images.map((_, idx) => (
                  <button key={idx} className={`post-card__dot ${idx === activeIdx ? 'post-card__dot--active' : ''}`} onClick={() => goTo(idx)} aria-label={`Photo ${idx + 1}`} />
                ))}
              </div>
              <div className="post-card__counter">{activeIdx + 1} / {images.length}</div>
            </>
          )}
        </div>
      )}

      {(post.caption || (post.tags && post.tags.length > 0)) && (
        <div className="post-card__body">
          {post.caption && <p className="post-card__caption">{post.caption}</p>}
          {post.tags && post.tags.length > 0 && (
            <div className="post-card__tags">
              {post.tags.map(t => <span key={t} className="badge badge-gold">#{t}</span>)}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function Gallery() {
  useScrollReveal();
  const [images,         setImages]         = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxData,   setLightboxData]  = useState(null);

  useEffect(() => {
    fetch('/api/gallery')
      .then(r => r.json())
      .then(data => { 
        if (data && data.items) {
          setImages(data.items);
          setCategories(data.categories || []);
        } else if (Array.isArray(data)) {
          setImages(data);
          setCategories([]);
        } else {
          setImages([]);
          console.error("Gallery API error:", data);
        }
        setLoading(false); 
      })
      .catch(() => setLoading(false));
  }, []);

  const getCategoryLabel = (id) => categories.find(c => c.id === id)?.label || id.charAt(0).toUpperCase() + id.slice(1);
  const filterTabs = [{ id: 'all', label: 'All Photos' }, ...categories];

  const filtered = useMemo(() => {
    const list = images || [];
    return activeCategory === 'all' ? list : list.filter(img => img && img.category === activeCategory);
  }, [images, activeCategory]);

  return (
    <main>
      <section className="page-hero" aria-labelledby="gallery-heading">
        <div className="container reveal reveal-up">
          <span className="section-label">Visual Archive</span>
          <h1 id="gallery-heading">Photo Gallery</h1>
          <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Twenty years in images — from academic halls in Yogyakarta to UN conferences in Geneva, ecological work in Gunung Kidul, and everything in between.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="gallery__filters">
            {filterTabs.map(cat => (
              <button
                key={cat.id}
                id={`gallery-filter-${cat.id}`}
                className={`gallery__filter-btn ${activeCategory === cat.id ? 'gallery__filter-btn--active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
                <span className="gallery__filter-count">
                  {cat.id === 'all' ? images.length : images.filter(img => img.category === cat.id).length}
                </span>
              </button>
            ))}
          </div>

          <p className="gallery__count">Showing <strong>{filtered.length}</strong> post{filtered.length !== 1 ? 's' : ''}</p>

          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-light)', padding: '4rem', fontStyle: 'italic' }}>Loading gallery…</div>
          ) : (
            <div className="gallery__feed">
              {filtered.map((post, i) => (
                <PostCard key={post.id} post={post} i={i} onLightbox={setLightboxData} getCategoryLabel={getCategoryLabel} />
              ))}
            </div>
          )}
        </div>
      </section>

      {lightboxData !== null && (
        <GalleryLightbox
          images={lightboxData.images}
          currentIndex={lightboxData.index}
          onClose={() => setLightboxData(null)}
          onPrev={() => setLightboxData(prev => ({ ...prev, index: Math.max(0, prev.index - 1) }))}
          onNext={() => setLightboxData(prev => ({ ...prev, index: Math.min(prev.images.length - 1, prev.index + 1) }))}
        />
      )}
    </main>
  );
}
